import axios from './axiosConfig';
import { Quote, SupplierRate } from '../types/quote.types';
import { UtilityService, CreateUtilityServiceDTO, UtilityStatus } from '../types/utility.types';

// Create a contract from a selected quote
export const createContractFromQuote = async (
  quote: Quote,
  selectedSupplier: SupplierRate,
  status: string
): Promise<UtilityService> => {
  // Calculate consumption values
  const isElectricity = quote.meterType === 'MPAN';
  const totalConsumption = isElectricity ? quote.eac : quote.aq;
  
  // Get day/night consumption from currentSupplier if available, otherwise use total
  const dayConsumption = quote.currentSupplier?.annualConsumption?.day || totalConsumption;
  const nightConsumption = quote.currentSupplier?.annualConsumption?.night || undefined;
  
  const contractData: CreateUtilityServiceDTO = {
    serviceType: isElectricity ? 'Electricity' : 'Gas',
    accountNumber: quote.meterIdentifier || '',
    status: status,
    supplier: selectedSupplier.supplier,
    contractStartDate: new Date().toISOString(),
    contractEndDate: new Date(
      Date.now() + (selectedSupplier.contractPeriodMonths * 30 * 24 * 60 * 60 * 1000)
    ).toISOString(),
    
    // Rate details from selected supplier
    dayRate: selectedSupplier.dayUnitRate,
    dayConsumption: dayConsumption,
    nightRate: selectedSupplier.nightUnitRate,
    nightConsumption: nightConsumption,
    standingCharge: selectedSupplier.standingCharge,
    
    // Legacy field for compatibility
    currentRate: (selectedSupplier.dayUnitRate ?? 0).toString(),
    eacAq: totalConsumption?.toString(),
    
    notes: `Contract created from Quote ${quote.quoteRef}. Selected supplier: ${selectedSupplier.supplierName}. Annual cost: ${selectedSupplier.annualExpenditure != null ? `£${selectedSupplier.annualExpenditure.toFixed(2)}` : 'N/A'}`,
    
    meterDetails: {
      mpanMprnSpid: quote.meterIdentifier,
      meterType: quote.meterType,
      serialNumber: quote.meterIdentifier // Use meter identifier as serial number
    },
    
    // Additional contract details
    contractValue: selectedSupplier.annualExpenditure?.toString(),
    annualUsage: totalConsumption?.toString(),
  };

  const response = await axios.post<UtilityService>(
    `/utility-services`,
    {
      ...contractData,
      clientId: quote.client?._id,
      propertyId: quote.property?._id,
      quoteId: quote._id, // Link back to the original quote
    }
  );

  return response.data;
};

// Get contracts created from quotes
export const getContractsFromQuotes = async (): Promise<UtilityService[]> => {
  const response = await axios.get<UtilityService[]>('/utility-services?fromQuote=true');
  return response.data || [];
};

// Update contract status — status is a catalogue-driven value
// (UtilityServiceStatus lookups); transitions are validated server-side.
export const updateContractStatus = async (
  contractId: string,
  status: UtilityStatus
): Promise<UtilityService> => {
  const response = await axios.patch<UtilityService>(
    `/utility-services/${contractId}`,
    { status }
  );
  return response.data;
};
